// Shared config + formatters for the Eval Results pages.
// Monochrome-friendly: every badge keeps a text label; color is a secondary
// cue only (grayscale-safe contrast between filled / outlined states).

export const VERDICT_CONFIG = {
  pass: { label: "PASS", bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
  fail: { label: "FAIL", bg: "bg-red-50", text: "text-red-700", border: "border-red-200" },
  partial: { label: "PARTIAL", bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  void: { label: "VOID", bg: "bg-gray-100", text: "text-gray-500", border: "border-gray-200" },
  infra: { label: "INFRA", bg: "bg-gray-100", text: "text-gray-500", border: "border-gray-300" },
  vacuous_suspect: { label: "VACUOUS?", bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
};

export function verdictCfg(verdict) {
  return (
    VERDICT_CONFIG[verdict] || {
      label: (verdict || "—").toUpperCase(),
      bg: "bg-gray-50",
      text: "text-gray-500",
      border: "border-gray-200",
    }
  );
}

export const RUN_KIND_LABELS = {
  smoke: "Smoke",
  baseline: "Baseline",
  gate2: "Gate 2",
  slice_gate: "Slice gate",
  canary: "Canary",
  monitor: "Monitor",
  rga: "RGA",
  llm_switch: "LLM switch",
};

export const FAMILY_LABELS = {
  q: "Quality",
  t: "Trajectory",
  p: "Precision",
  e: "E2E",
  c: "Contract",
  g: "Guardrail",
  live: "Live",
};

// Deterministic color per pack_version for chart dots. Palette is
// sequential-ish so adjacent versions stay distinguishable in grayscale
// (differ in lightness, not only hue).
const PACK_VERSION_PALETTE = [
  "#111827", // gray-900
  "#6366F1", // indigo-500
  "#0EA5E9", // sky-500
  "#10B981", // emerald-500
  "#F59E0B", // amber-500
  "#EF4444", // red-500
  "#8B5CF6", // violet-500
  "#6B7280", // gray-500
];

export function packVersionColor(version, orderedVersions) {
  const idx = Math.max(0, (orderedVersions || []).indexOf(version));
  return PACK_VERSION_PALETTE[idx % PACK_VERSION_PALETTE.length];
}

export function formatPct(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return `${(Number(value) * 100).toFixed(0)}%`;
}

export function formatMetric(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return Number(value).toFixed(2);
}

export function formatUsd(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return `$${Number(value).toFixed(2)}`;
}

export function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "2-digit",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function timeAgo(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function shortSha(sha, len = 8) {
  return sha ? String(sha).slice(0, len) : "—";
}
